import React, { createContext, useCallback, useEffect, useReducer } from 'react';
import axios from 'axios';

// third-party
import { Chance } from 'chance';
import { jwtDecode } from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project imports
import Loader from 'components/Loader';
import axiosServices, { setUnauthorizedHandler } from 'utils/axios';
import { BASE_URL } from 'config';
import { AuthProps, JWTContextType } from 'types/auth';
import { KeyedObject } from 'types/root';

const chance = new Chance();

// constant
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

const TOKEN_STORAGE_KEY = 'serviceToken';
const USER_INFO_STORAGE_KEY = 'userInformation';
const USER_ACCESS_STORAGE_KEY = 'userAccess';
const AUTH_USER_STORAGE_KEY = 'authUser';
const REMEMBER_ME_STORAGE_KEY = 'keepMeSignedIn';
const APP_OWNER_SESSION_KEY = 'appOwnerFingerprint';

const isBrowser = typeof window !== 'undefined';

type StoredTokenInfo = {
  token: string | null;
  rememberMe?: boolean;
};

const getStoredToken = (): StoredTokenInfo => {
  if (!isBrowser) {
    return { token: null, rememberMe: undefined };
  }

  const localToken = window.localStorage.getItem(TOKEN_STORAGE_KEY);
  if (localToken) {
    return { token: localToken, rememberMe: true };
  }

  const sessionToken = window.sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (sessionToken) {
    return { token: sessionToken, rememberMe: false };
  }

  return { token: null, rememberMe: undefined };
};

const parseJSON = <T,>(value: string | null): T | null => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value) as T;
  } catch (error) {
    console.error('Failed to parse JSON from storage', error);
    return null;
  }
};

interface DecodedToken extends KeyedObject {
  exp?: number;
}

const verifyToken = (serviceToken?: string | null): boolean => {
  if (!serviceToken) {
    return false;
  }

  try {
    const decoded = jwtDecode<DecodedToken>(serviceToken);
    if (!decoded || typeof decoded !== 'object') {
      return false;
    }

    if (!decoded.exp) {
      return true;
    }

    const expiration = Number(decoded.exp) * 1000;
    if (Number.isNaN(expiration)) {
      return true;
    }

    return expiration > Date.now();
  } catch (error) {
    console.error('Unable to verify token', error);
    return false;
  }
};

const setSession = (serviceToken?: string | null, rememberMe?: boolean) => {
  if (!isBrowser) {
    return;
  }

  if (serviceToken) {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, serviceToken);

    if (rememberMe === false) {
      window.sessionStorage.setItem(TOKEN_STORAGE_KEY, serviceToken);
    } else {
      window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    }

    if (typeof rememberMe === 'boolean') {
      window.localStorage.setItem(REMEMBER_ME_STORAGE_KEY, rememberMe ? 'true' : 'false');
    }

    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    axiosServices.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    window.localStorage.removeItem(USER_INFO_STORAGE_KEY);
    window.localStorage.removeItem(USER_ACCESS_STORAGE_KEY);
    window.localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    window.localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);

    delete axios.defaults.headers.common.Authorization;
    delete axiosServices.defaults.headers.common.Authorization;
  }
};

type OwnerFingerprint = {
  userId: string;
  tokenHash: string;
};

const makeOwnerFingerprint = (user: KeyedObject | null | undefined, token: string | null | undefined): OwnerFingerprint => {
  try {
    const userId = (user && (user._id || user.id || user.username)) || '';
    const tokenHash = token ? String(token).slice(-12) : '';
    return { userId, tokenHash };
  } catch {
    return { userId: '', tokenHash: '' };
  }
};

const ensureSessionOwner = (user: KeyedObject | null | undefined, token: string | null | undefined) => {
  if (!isBrowser) return;

  const current = makeOwnerFingerprint(user, token);
  const raw = window.sessionStorage.getItem(APP_OWNER_SESSION_KEY);

  let saved: OwnerFingerprint | null = null;
  try {
    saved = raw ? (JSON.parse(raw) as OwnerFingerprint) : null;
  } catch {
    saved = null;
  }

  const changed = !saved || saved.userId !== current.userId || saved.tokenHash !== current.tokenHash;

  if (changed) {
    try {
      window.sessionStorage.clear();
    } catch (error) {
      console.warn('sessionStorage.clear() failed', error);
    }
    try {
      window.sessionStorage.setItem(APP_OWNER_SESSION_KEY, JSON.stringify(current));
    } catch (error) {
      console.warn('Marking session owner failed', error);
    }
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const checkUserInformation = useCallback(
    (user: KeyedObject | null | undefined, attempt = 0) => {
      if (!isBrowser) {
        return;
      }

      const storedInformation = window.localStorage.getItem(USER_INFO_STORAGE_KEY);

      if (storedInformation) {
        const parsedInformation = parseJSON<KeyedObject>(storedInformation);
        const resolvedUser = (user as KeyedObject | null | undefined) ?? parsedInformation ?? null;

        if (resolvedUser) {
          window.localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(resolvedUser));
        }

        dispatch({
          type: LOGIN,
          payload: {
            isLoggedIn: true,
            user: resolvedUser
          }
        });
        return;
      }

      if (attempt < 10) {
        setTimeout(() => checkUserInformation(user, attempt + 1), 200);
        return;
      }

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: (user as KeyedObject | null | undefined) ?? null
        }
      });
    },
    [dispatch]
  );

  const logout = useCallback(() => {
    setSession(null);
    try {
      if (isBrowser) {
        window.sessionStorage.clear();
      }
    } catch (error) {
      console.warn('sessionStorage.clear() failed on logout', error);
    }
    dispatch({ type: LOGOUT });
  }, [dispatch]);

  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    const init = async () => {
      try {
        const { token: storedToken, rememberMe: storedRememberMe } = getStoredToken();
        const rememberRaw = window.localStorage.getItem(REMEMBER_ME_STORAGE_KEY);
        const rememberPreference = rememberRaw === null ? storedRememberMe : rememberRaw === 'true';

        if (storedToken && verifyToken(storedToken)) {
          setSession(storedToken, rememberPreference);

          let user = parseJSON<KeyedObject>(window.localStorage.getItem(AUTH_USER_STORAGE_KEY));

          if (!user) {
            const response = await axios.get(`${BASE_URL}/api/user-info`);
            user = (response.data as KeyedObject) ?? null;

            if (user) {
              window.localStorage.setItem(USER_INFO_STORAGE_KEY, JSON.stringify(user));

              const access = (user as KeyedObject)?.access;
              if (access) {
                window.localStorage.setItem(USER_ACCESS_STORAGE_KEY, JSON.stringify(access));
              }

              window.localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
            }
          }

          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: user ?? null
            }
          });

          ensureSessionOwner(user, storedToken);
        } else {
          setSession(null);
          dispatch({
            type: LOGOUT
          });
        }
      } catch (error) {
        console.error(error);
        setSession(null);
        dispatch({
          type: LOGOUT
        });
      }
    };

    void init();
  }, [dispatch]);

  const login = async (username: string, password: string, rememberMe = true) => {
    const response = await axios.post(`${BASE_URL}/auth/login`, { username, password });
    const data = response.data as KeyedObject;
    const user = (data.user as KeyedObject | null | undefined) ?? null;
    const token = (data.token as string | undefined) ?? (data.serviceToken as string | undefined);

    if (!token) {
      throw new Error('Login response did not include an access token.');
    }

    if (isBrowser && user) {
      window.localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
    }

    setSession(token, rememberMe);
    ensureSessionOwner(user, token);

    let resolvedUser = user;
    try {
      const infoResponse = await axios.get(`${BASE_URL}/api/user-info`);
      resolvedUser = (infoResponse.data as KeyedObject) ?? user;
    } catch (error) {
      console.error('Failed to fetch user info on login', error);
    }

    const isConsultantUser = Boolean(
      resolvedUser?.Consultant?.isConsultant ||
        resolvedUser?.isConsultant ||
        String(resolvedUser?.userType || '').toLowerCase().includes('consult')
    );

    const sanitizedUser = (resolvedUser
      ? {
          ...resolvedUser,
          ...(isConsultantUser
            ? { city: undefined, cityName: undefined, Consultant: { ...(resolvedUser as any)?.Consultant, isConsultant: true } }
            : {})
        }
      : null) as KeyedObject | null;

    if (sanitizedUser) {
      window.localStorage.setItem(USER_INFO_STORAGE_KEY, JSON.stringify(sanitizedUser));
      const access = (sanitizedUser as KeyedObject)?.access;
      if (access) {
        window.localStorage.setItem(USER_ACCESS_STORAGE_KEY, JSON.stringify(access));
      }
      window.localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(sanitizedUser));
    }

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: sanitizedUser ?? null
      }
    });
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data as KeyedObject[];

    if (isBrowser && window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
      const localUsers = window.localStorage.getItem('users');
      const parsed = parseJSON<KeyedObject[]>(localUsers);
      users = [
        ...(parsed ?? []),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }

    if (isBrowser) {
      window.localStorage.setItem('users', JSON.stringify(users));
    }
  };

  const resetPassword = async (_email: string) => {
    // Intentionally blank; implement when backend is ready.
  };

  const updateProfile = () => {};

  useEffect(() => {
    setUnauthorizedHandler(() => {
      logout();
      if (isBrowser && !window.location.pathname.includes('/login')) {
        window.location.replace('/login');
      }
    });

    return () => setUnauthorizedHandler(null);
  }, [logout]);

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider value={{ ...state, login, logout, register, resetPassword, updateProfile }}>
      {children}
    </JWTContext.Provider>
  );
};

export default JWTContext;
